import { Box, Typography } from '@mui/material';
import {
  BigSectionRow,
  EntityHeaderMetaFieldLayout,
  SectionRow,
  ShowSection,
  MediumFormRow,
} from 'framework/ui/layout';
import { useShowContext } from 'framework/show';
import { ControlledList, DataGridLayout } from 'framework/list';
import CancelVacancyBeforeReviewButton from '../../shared/button/CancelVacancyBeforeReviewButton';
import { CommaListField, DateField, EnumField, HtmlField, TextField } from 'framework/ui/field';
import { HIRING_MODE, POSITION_STATUS_OPTIONS, POSITION_TYPE_OPTIONS } from 'domain/shared/newVacancy';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import VacancyProjectsSwiper from './shared/VacancyProjectsSwiper';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import WorkingConditionsSwiper from './shared/WorkingConditionsSwiper';
import type { Data } from 'framework/types';
import LabelStatus from '../shared/LabelStatus';
import LabelAllHired from '../shared/LabelAllHired';
import LabelCandidateRejected from '../shared/LabelCandidateRejected';
import VacancyCloseDateListField from './shared/VacancyCloseDateListField';
import ApproveVacancyButton from 'domain/recruiting/newVacancy/shared/button/ApproveVacancyButton';
import CloseVacancyButton from 'domain/recruiting/newVacancy/shared/button/CloseVacancyButton';
import RequestVacancyCancellationButton from 'domain/recruiting/newVacancy/shared/button/RequestVacancyCancellationButton';
import ResolveVacancyCancellationRequestButton from 'domain/recruiting/newVacancy/shared/button/ResolveVacancyCancellationRequestButton';
import { ShowDate } from 'framework/ui/ui-builder';
import { DATE_FORMAT } from 'framework/utils';
import { UiThemeOverrider } from 'framework/uiTheme';
import { ENGLISH_LEVEL_SHORT } from 'domain/shared/core/constants';

const ViewVacancyContent = ({ setGeneralContextData }: any) => {
  const { data } = useShowContext();
  const { positions, status } = data.getData();

  const createdAt = data.getValueBySource('createdAt');

  return (
    <>
      <Box
        sx={{
          display: 'flex',

          gap: '20px',
          alignItems: 'center',

          '& > section': {
            flex: '0 0 49%',
            maxWidth: '49%',
            height: '280px',
            position: 'relative',
          },

          '@media (max-width: 1440px)': {
            flexWrap: 'wrap',
            gap: '0',

            '& > section': {
              flex: '0 0 100%',
              maxWidth: '100%',
              height: 'auto',
            },
          },
        }}
      >
        <ShowSection title="Positions">
          <ControlledList data={{ items: positions, totalElements: positions.length, totalPages: 1 }}>
            <DataGridLayout
              sxPaginationWrapper={{ paddingTop: '15px' }}
              sxTableWrapper={{
                maxHeight: '130px',
                '& > .MuiTable-root': { minWidth: 'auto !important' },
              }}
              isSmallTable
            >
              <EnumField source="type" label="Type" options={POSITION_TYPE_OPTIONS} width="15%" />
              <EnumField source="status" label="Status" options={POSITION_STATUS_OPTIONS} width="15%" />
              <DateField source="forecastDate" label="Forecast date" />

              <VacancyCloseDateListField
                setGeneralContextData={setGeneralContextData}
                vacancyStatus={status}
                label="Close date"
              />

              <DateField source="startWorkingDate" label="Start working date" />
              <LabelCandidateRejected />
            </DataGridLayout>
          </ControlledList>
        </ShowSection>

        <ShowSection
          actions={[
            <ApproveVacancyButton statusSource="status" setGeneralContextData={setGeneralContextData} />,
            <CloseVacancyButton setGeneralContextData={setGeneralContextData} />,
            <CancelVacancyBeforeReviewButton statusSource="status" setGeneralContextData={setGeneralContextData} />,
            <RequestVacancyCancellationButton statusSource="status" setGeneralContextData={setGeneralContextData} />,
            <ResolveVacancyCancellationRequestButton
              statusSource="status"
              setGeneralContextData={setGeneralContextData}
            />,
          ]}
          justifyContent="space-between"
          title={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <Typography variant="h4">
                {data.competencyRange} {data.specializationName}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexWrap: 'wrap',
                  marginRight: '60px',
                }}
              >
                <LabelStatus status={data.status} />
                <LabelAllHired status={data.status} areAllHired={data.areAllHired} />
              </Box>
            </Box>
          }
        >
          <SectionRow spacing={{}}>
            <EnumField source="hiringMode" label="Hiring mode" options={HIRING_MODE} />
            <DateField
              isVisible={(showData: Data) => !showData.getValueBySource('isAsap')}
              source="targetHiringDate"
              label="Target hiring"
            />
            <TextField
              isVisible={(showData: Data) => showData.getValueBySource('isAsap')}
              value="Asap"
              label="Target hiring"
            />
            <DateField source="expirationDate" label="Expiration" />
          </SectionRow>

          <SectionRow spacing={{}}>
            <EnumField source="mandatoryRequirements.englishLevel" label="English" options={ENGLISH_LEVEL_SHORT} />
            <EmployeeLinkFieldWithNameLoading label="DRI" source="responsiblePerson" />
            <EmployeeLinkFieldWithNameLoading label="Coordinator" source="coordinator" />
          </SectionRow>

          <UiThemeOverrider overriddenLayouts={{ BaseFieldLayout: EntityHeaderMetaFieldLayout }}>
            <Box
              sx={{
                position: 'absolute',
                textAlign: 'right',
                color: 'text.secondary',
                right: '20px',
                bottom: '10px',
                padding: '0',
                fontSize: '10px',
              }}
            >
              <TextField
                label="Created"
                value={
                  createdAt && (
                    <>
                      <ShowDate dateUTC={createdAt} format={DATE_FORMAT.FULL} />
                    </>
                  )
                }
              />
            </Box>
          </UiThemeOverrider>
        </ShowSection>
      </Box>

      <Box
        sx={{
          display: 'flex',

          gap: '20px',
          alignItems: 'center',

          '& > section': {
            flex: '0 0 49%',
            maxWidth: '49%',
            height: '270px',
          },

          '@media (max-width: 1440px)': {
            flexWrap: 'wrap',
            gap: '0',

            '& > section': {
              flex: '0 0 100%',
              maxWidth: '100%',
              height: 'auto',
            },
          },
        }}
      >
        <ShowSection title="Mandatory requirements">
          <Box
            sx={{
              maxHeight: '120px',
              overflow: 'auto',
              mb: '10px',
            }}
          >
            <HtmlField source="mandatoryRequirements.description" label="" />
          </Box>

          <MediumFormRow
            sx={{
              '> .MuiGrid-item': {
                pl: 0,
              },
              ml: 0,
            }}
          >
            <CommaListField label="Skills" source="mandatoryRequirements.skills" />
            <TextField label="Min experience (years)" source="mandatoryRequirements.requiredExperienceInYears" />
          </MediumFormRow>
        </ShowSection>

        <ShowSection title="Working conditions">
          <WorkingConditionsSwiper />
        </ShowSection>
      </Box>

      <Box
        sx={{
          display: 'flex',

          flexWrap: 'wrap',
          gap: '0 20px',
          alignItems: 'center',

          '& > section': {
            flex: '0 0 49%',
            maxWidth: '49%',
            height: '365px',
          },

          '@media (max-width: 1440px)': {
            flexWrap: 'wrap',
            gap: '0',

            '& > section': {
              flex: '0 0 100%',
              maxWidth: '100%',
              height: 'auto',
            },
          },
        }}
      >
        <ShowSection title="Responsibilities">
          <Box
            sx={{
              maxHeight: '265px',
              overflow: 'auto',
            }}
          >
            <HtmlField source="responsibilities" label="" />
          </Box>
        </ShowSection>

        <ShowSection isVisible={showData => showData.getValueBySource('projects')?.length > 0}>
          <VacancyProjectsSwiper />
        </ShowSection>

        <ShowSection
          title="Optional requirements"
          isVisible={showData => showData.getValueBySource('optionalRequirements')}
        >
          <Box
            sx={{
              maxHeight: '210px',
              overflow: 'auto',
              mb: '10px',
            }}
          >
            <HtmlField source="optionalRequirements.description" label="" />
          </Box>

          <BigSectionRow spacing={{}}>
            <CommaListField label="Skills" source="optionalRequirements.skills" />
          </BigSectionRow>
        </ShowSection>

        <ShowSection title="Notes" isVisible={showData => showData.getValueBySource('notes')}>
          <Box
            sx={{
              maxHeight: '210px',
              overflow: 'auto',
              mb: '10px',
            }}
          >
            <HtmlField source="notes.description" label="" />
          </Box>

          <AttachmentsListField source="notes.attachmentIds" label="Attachments" />
        </ShowSection>
      </Box>
    </>
  );
};

export default ViewVacancyContent;
